import {useMemo, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import * as FileConstants from 'folder-items/FileConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {NameTemplateType} from 'name-templates/NameTemplateConstants';
import {PrefixedSchema} from 'schemas/SchemaConstants';
import {getTagsToSet, hasValueForAllRequiredFields} from 'schemas/tags/TagUtil';
import {CreateModalStudyQuery_studySchema_StudySchemaNode_nameTemplates_NameTemplateNode as NameTemplate} from 'studies/sidepanel/__generated/CreateModalStudyQuery';
import {CreateStudyModalOptionalParams} from 'studies/sidepanel/NewStudyDropdown';
import {useCanChangeStudyableAssignmentFolderQuery} from 'studies/studyable/useCanChangeStudyableAssignmentQuery';
import {
  EMPTY_PREFIX,
  EXPERIMENT_NAME_MAX_LENGTH,
  STUDY_DESCRIPTION_MAX_LENGTH,
  STUDY_DISPLAY_ID_MAX_LENGTH,
  STUDY_NAME_MAX_LENGTH,
} from 'studies/StudyConstants';
import {CreateStudyParams} from 'studies/StudyDAO';
import useFormInput from 'ui-library/hooks/useFormInput';
import strUtil from 'util/strUtil';
import {InputChangeEvent} from 'util/Types';

import {TypedStudyAdapter, TypedStudyStateBase} from './TypedStudyTypes';

export type DraftStudySchema = Pick<PrefixedSchema, 'id' | 'name' | 'fields' | 'prefix'> & {
  nameTemplates: Array<NameTemplate>;
};

// TODO(BNCH-100597): Remove getStudyMaxNameLength once PD studies are multi-experiment and no longer share the study name with the experiment name
export function getStudyMaxNameLength(type: StudyType): number {
  return type === StudyType.PROCESS_DEVELOPMENT ? EXPERIMENT_NAME_MAX_LENGTH : STUDY_NAME_MAX_LENGTH;
}
/**
 * Manages the state of a draft study, exposing a CreateStudyParams object that can be used to create a study.
 *
 * The hook itself exposes callbacks to manage the study metadata, and validates that the study metadata is
 * complete. For typed-study specific fields, it defer to the TypedStudyAdapters useTypedStudyState to produce
 * an experimentParam that can be augmented to the studyParams object.
 *
 */
export function useDraftStudy<TExperimentKey extends keyof CreateStudyParams | never = never>(
  schema: DraftStudySchema,
  initialValues: CreateStudyModalOptionalParams,
  adapter: TypedStudyAdapter<TExperimentKey, TypedStudyStateBase<TExperimentKey>>,
  typedStudyState: TypedStudyStateBase<TExperimentKey>
): {
  inputs: {
    nameFormInput: ReturnType<typeof useFormInput>;
    descriptionFormInput: ReturnType<typeof useFormInput>;
    folderInput: {
      value: string | null | undefined;
      onChange: (e: {target: {value: string | null}}) => void;
    };
    tagsInput: {
      tags: Array<FileConstants.SchemaTag>;
      onChange: (e: Array<FileConstants.SchemaTag>) => void;
    };
    displayIdFormInput: ReturnType<typeof useFormInput>;
  };
  validation: {
    isNameValid: boolean;
    isDescriptionValid: boolean;
    isStudyMetadataValid: boolean;
    isExperimentParamValid: boolean;
    isDisplayIdValid: boolean;
    canChangeStudyableAssignment: boolean | null;
  };
  /** CreateStudyParams if metadata and experimentParam pass validation. */
  studyParams: CreateStudyParams | null;
} {
  const nameFormInput = useFormInput('');
  const descriptionFormInput = useFormInput('');
  const displayIdFormInput = useFormInput('');
  const [folderId, setFolderId] = useState(
    initialValues.initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.STUDIES)
  );
  const canChangeStudyableAssignment = useCanChangeStudyableAssignmentFolderQuery(folderId);
  const folderInput = {
    value: folderId,
    onChange: (e: InputChangeEvent) => {
      setFolderId(e.target.value);
    },
  };
  const studyNameTemplates = schema.nameTemplates.filter(
    (template) => template.templateType === NameTemplateType.STUDY
  );
  assertAndContinue(studyNameTemplates.length <= 1, 'Multiple study name templates are not supported');
  // when a name template is disabled, only the nameTemplateParts are removed from nameTemplates[0]
  const hasNameTemplate =
    studyNameTemplates.length > 0 ? studyNameTemplates[0].nameTemplateParts.length > 0 : false;
  const [tags, setTags] = useState<Array<FileConstants.SchemaTag>>([]);
  const tagsInput = {tags, onChange: setTags};
  // Validation on study name length is done on backend for names generated from a template
  const isNameValid = nameFormInput.value.length <= getStudyMaxNameLength(adapter.type) || hasNameTemplate;
  const isDescriptionValid = descriptionFormInput.value.length <= STUDY_DESCRIPTION_MAX_LENGTH;
  const isDisplayIdValid = displayIdFormInput.value.length <= STUDY_DISPLAY_ID_MAX_LENGTH;
  const studyMetadata = useMemo(() => {
    const displayIdRequired = schema.prefix === EMPTY_PREFIX;
    if (!isNameValid || !isDescriptionValid || !isDisplayIdValid) {
      return null;
    }
    if (
      folderId == null ||
      (strUtil.isEmpty(nameFormInput.value) && !hasNameTemplate) ||
      !canChangeStudyableAssignment
    ) {
      return null;
    }
    if (schema.fields == null || !hasValueForAllRequiredFields(tags, schema.fields)) {
      return null;
    }
    if (displayIdRequired && strUtil.isEmpty(displayIdFormInput.value)) {
      return null;
    }
    const experimentKeyPart =
      adapter.experimentKey != null ? {[adapter.experimentKey]: typedStudyState.experimentParam} : {};
    return {
      name: hasNameTemplate ? undefined : nameFormInput.value,
      description: descriptionFormInput.value,
      folderId,
      tags: getTagsToSet(tags),
      studySchemaId: schema.id,
      displayId: displayIdRequired ? displayIdFormInput.value : undefined,
      ...experimentKeyPart,
    };
  }, [
    schema.prefix,
    schema.fields,
    schema.id,
    isNameValid,
    isDescriptionValid,
    isDisplayIdValid,
    folderId,
    nameFormInput.value,
    hasNameTemplate,
    canChangeStudyableAssignment,
    adapter.experimentKey,
    tags,
    displayIdFormInput.value,
    descriptionFormInput.value,
    typedStudyState.experimentParam,
  ]);
  const studyParams = useMemo(() => {
    if (
      studyMetadata == null ||
      (typedStudyState.experimentParam == null && adapter.creation.experimentParamRequired)
    ) {
      return null;
    }
    if (adapter.experimentKey != null) {
      return {
        ...studyMetadata,
        [adapter.experimentKey]: typedStudyState.experimentParam,
      };
    }
    return studyMetadata;
  }, [studyMetadata, adapter.experimentKey, adapter.creation.experimentParamRequired, typedStudyState]);

  return {
    inputs: {
      nameFormInput,
      descriptionFormInput,
      folderInput,
      tagsInput,
      displayIdFormInput,
    },
    validation: {
      isNameValid,
      isDescriptionValid,
      isStudyMetadataValid: studyMetadata != null,
      isExperimentParamValid:
        typedStudyState.experimentParam != null || !adapter.creation.experimentParamRequired,
      isDisplayIdValid,
      canChangeStudyableAssignment,
    },
    studyParams,
  };
}
